/**
 * `AutomationsSource` over the M6 api. Structurally typed, like M5's, so this module never
 * imports server code.
 */
import type { AutomationRecipe } from '@elizmonday/shared';
import type {
  AutomationDetailView,
  AutomationSummaryView,
  AutomationsSource,
  PickerDataView,
  RunRowView,
  RunStepView,
  TemplateGalleryView,
} from './types';

interface Query<TIn, TOut> {
  query(input: TIn): Promise<TOut>;
}
interface Mutate<TIn, TOut> {
  mutate(input: TIn): Promise<TOut>;
}

export interface AutomationsTrpc {
  automations: {
    listForBoard: Query<{ boardId: string; search?: string; state?: string; ownerId?: string; app?: string }, AutomationSummaryView[]>;
    get: Query<{ id: string }, AutomationDetailView>;
    create: Mutate<{ boardId: string; recipe: AutomationRecipe; title?: string }, { id: string }>;
    update: Mutate<{ id: string; recipe?: AutomationRecipe; title?: string; description?: string | null }, { id: string }>;
    setActive: Mutate<{ id: string; active: boolean }, { id: string; active: boolean; state: string }>;
    duplicate: Mutate<{ id: string; toBoardId?: string }, { id: string }>;
    delete: Mutate<{ id: string }, unknown>;
    pickers: Query<{ boardId: string }, PickerDataView>;
    templates: Query<{ boardId: string }, TemplateGalleryView>;
    runs: {
      list: Query<{ boardId: string; automationId?: string; status?: string; includeSkipped?: boolean }, RunRowView[]>;
      get: Query<{ runId: string }, { steps: RunStepView[] }>;
    };
  };
  /** P-200: the column picker's "+ Add a new column" row, which is `columns.create`. */
  columns: {
    create: Mutate<{ boardId: string; type: string; title?: string }, { id: string; type: string; title: string }>;
  };
}

export function createAutomationsSource(trpc: AutomationsTrpc): AutomationsSource {
  return {
    list: (input) => trpc.automations.listForBoard.query(input),
    get: (id) => trpc.automations.get.query({ id }),
    create: (input) => trpc.automations.create.mutate(input),
    update: (input) => trpc.automations.update.mutate(input),
    setActive: (input) => trpc.automations.setActive.mutate(input),
    duplicate: (input) => trpc.automations.duplicate.mutate(input),
    remove: (id) => trpc.automations.delete.mutate({ id }),
    runs: (input) => trpc.automations.runs.list.query(input),
    runSteps: async (runId) => (await trpc.automations.runs.get.query({ runId })).steps,
    pickers: (boardId) => trpc.automations.pickers.query({ boardId }),
    templates: (boardId) => trpc.automations.templates.query({ boardId }),
    createColumn: (input) => trpc.columns.create.mutate(input),
  };
}
